import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  Avatar,
  Typography,
  Popover,
  Box,
  Button,
  Chip,
  IconButton,
  Badge,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import {
  Phone,
  PhoneIncoming,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Building2,
  StickyNote,
  Headphones,
  RefreshCw,
  X,
  AlarmClock,
  Volume2,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import clsx from "clsx";

import { useToast } from "../Toast";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";
import soundService from "../../services/soundService";

/**
 * CallbackWidget - Widget de Callbacks Pendentes
 * 
 * FASE 3: UI de Callback
 * Ref: voice-ai-ivr/openspec/changes/intelligent-voice-handoff/
 * 
 * Funcionalidades:
 * - Badge com contador de callbacks pendentes
 * - Popover com lista de callbacks
 * - Ações: Ligar Agora, Adiar, Dispensar
 * - Atualização em tempo real via Socket.IO
 */

const useStyles = makeStyles((theme) => ({
  // Popover
  popoverPaper: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0,0,0,0.4)"
      : "0 8px 32px rgba(0,0,0,0.12)",
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
  
  // Header
  popoverHeader: {
    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark || '#e68900'} 100%)`,
    color: "#fff",
    padding: theme.spacing(0.875, 1.25),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  headerIcon: {
    width: 24,
    height: 24,
    borderRadius: 5,
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#fff",
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: "0.65rem",
    color: "rgba(255,255,255,0.65)",
    marginTop: 1,
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  headerButton: {
    color: "rgba(255,255,255,0.7)",
    padding: 3,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.12)",
      color: "#fff",
    },
  },
  
  // List
  listContainer: {
    maxHeight: 400,
    overflow: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  
  // Empty state
  emptyState: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  emptyIcon: {
    width: 32,
    height: 32,
    color: theme.palette.text.disabled,
    opacity: 0.2,
  },
  
  // Callback Card
  callbackCard: {
    position: "relative",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  callbackCardUrgent: {
    borderLeft: `3px solid ${theme.palette.error.main}`,
  },
  callbackCardNormal: {
    borderLeft: `3px solid ${theme.palette.warning.main}`,
  },
  callbackCardScheduled: {
    borderLeft: `3px solid ${theme.palette.info.main}`,
  },
  
  // Card header
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardAvatar: {
    width: 36,
    height: 36,
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.main,
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardSubtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  
  // Card info
  cardInfo: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  infoChip: {
    height: 22,
    fontSize: "0.7rem",
    fontWeight: 500,
  },
  
  // Card reason
  cardReason: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    marginBottom: theme.spacing(1),
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  
  // Card actions
  cardActions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
  },
  actionButton: {
    fontSize: "0.75rem",
    textTransform: "none",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 6,
    minWidth: 0,
  },
  callButton: {
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  snoozeButton: {
    color: theme.palette.text.secondary,
    borderColor: alpha(theme.palette.divider, 0.3),
  },
  dismissButton: {
    color: theme.palette.text.secondary,
  },
  
  // Waiting time badge
  waitingBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
  },
  waitingUrgent: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  
  // Loading
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
}));

// Formata número de telefone para exibição
const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  // Remove o 55 do início se presente
  let clean = phone.replace(/\D/g, "");
  if (clean.startsWith("55") && clean.length > 11) {
    clean = clean.substring(2);
  }
  // Formata: (XX) XXXXX-XXXX
  if (clean.length === 11) {
    return `(${clean.substring(0, 2)}) ${clean.substring(2, 7)}-${clean.substring(7)}`;
  } else if (clean.length === 10) {
    return `(${clean.substring(0, 2)}) ${clean.substring(2, 6)}-${clean.substring(6)}`;
  }
  return phone;
};

// Calcula tempo de espera em minutos
const getWaitingMinutes = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  return Math.floor((now - created) / 60000);
};

// Determina urgência baseado no tempo de espera
const getUrgencyClass = (classes, callback) => {
  const waitingMinutes = getWaitingMinutes(callback.createdAt);
  
  if (callback.callbackScheduledAt) {
    return classes.callbackCardScheduled;
  }
  
  if (waitingMinutes >= 30) {
    return classes.callbackCardUrgent;
  }
  
  return classes.callbackCardNormal;
};

// Componente de card de callback
const CallbackCard = ({ callback, onCall, onSnooze, onDismiss, loading }) => {
  const classes = useStyles();
  const waitingMinutes = getWaitingMinutes(callback.createdAt);
  const isUrgent = waitingMinutes >= 30;
  
  return (
    <Box className={clsx(classes.callbackCard, getUrgencyClass(classes, callback))}>
      {/* Badge de tempo de espera */}
      <Box className={clsx(classes.waitingBadge, isUrgent && classes.waitingUrgent)}>
        <Clock size={12} />
        {waitingMinutes < 60
          ? `${waitingMinutes} min`
          : `${Math.floor(waitingMinutes / 60)}h ${waitingMinutes % 60}min`}
      </Box>
      
      {/* Header */}
      <Box className={classes.cardHeader}>
        <Avatar className={classes.cardAvatar}>
          <PhoneIncoming size={18} />
        </Avatar>
        <Box className={classes.cardContent}>
          <Typography className={classes.cardTitle}>
            {formatPhoneNumber(callback.callbackNumber)}
          </Typography>
          <Typography className={classes.cardSubtitle}>
            {callback.contact?.name && (
              <>
                <User size={12} />
                {callback.contact.name}
              </>
            )}
          </Typography>
        </Box>
      </Box>
      
      {/* Info chips */}
      <Box className={classes.cardInfo}>
        {callback.callbackIntendedForName && (
          <Chip
            icon={<Headphones size={12} />}
            label={`Para: ${callback.callbackIntendedForName}`}
            className={classes.infoChip}
            size="small"
            variant="outlined"
          />
        )}
        {callback.callbackDepartment && (
          <Chip
            icon={<Building2 size={12} />}
            label={callback.callbackDepartment}
            className={classes.infoChip}
            size="small"
            variant="outlined"
          />
        )}
        {callback.callbackScheduledAt && (
          <Chip
            icon={<Clock size={12} />}
            label={`Agendado: ${format(new Date(callback.callbackScheduledAt), "HH:mm", { locale: ptBR })}`}
            className={classes.infoChip}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      
      {/* Motivo */}
      {callback.callbackReason && (
        <Typography className={classes.cardReason}>
          <StickyNote size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
          {callback.callbackReason}
        </Typography>
      )}
      
      {/* Resumo de voz */}
      {callback.voiceSummary && (
        <Typography className={classes.cardReason}>
          <Volume2 size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
          {callback.voiceSummary}
        </Typography>
      )}
      
      {/* Ações */}
      <Box className={classes.cardActions}>
        <Tooltip title="Dispensar callback">
          <span>
            <Button
              size="small"
              className={clsx(classes.actionButton, classes.dismissButton)}
              onClick={() => onDismiss(callback.id)}
              disabled={loading}
            >
              <XCircle size={18} />
            </Button>
          </span>
        </Tooltip>
        
        <Button
          size="small"
          variant="outlined"
          className={clsx(classes.actionButton, classes.snoozeButton)}
          startIcon={<AlarmClock size={18} />}
          onClick={() => onSnooze(callback.id)}
          disabled={loading}
        >
          Adiar
        </Button>
        
        <Button
          size="small"
          variant="contained"
          className={clsx(classes.actionButton, classes.callButton)}
          startIcon={<Phone size={18} />}
          onClick={() => onCall(callback)}
          disabled={loading}
        >
          Ligar Agora
        </Button>
      </Box>
    </Box>
  );
};

const CallbackWidget = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { showToast } = useToast();
  
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Só exibir se o usuário tem VoIP habilitado
  const hasVoip = !!(user?.voipEnabled && user?.voipExtension);

  const open = Boolean(anchorEl);
  const id = open ? "callback-popover" : undefined;
  
  // Carregar callbacks pendentes
  const loadCallbacks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/callbacks", {
        params: {
          status: "pending,notified",
          limit: 20
        }
      });
      setCallbacks(data.callbacks || []);
    } catch (error) {
      console.error("Erro ao carregar callbacks:", error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Carregar ao montar (apenas se VoIP habilitado)
  useEffect(() => {
    if (!hasVoip) return;
    loadCallbacks();
  }, [loadCallbacks, hasVoip]);
  
  // Escutar eventos de socket (apenas se VoIP habilitado)
  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId || !hasVoip) return;
    if (!socket) return;
    
    // IMPORTANTE: Entrar no room "notification" para receber eventos
    // O backend emite para io.of(companyId).to("notification")
    const onConnect = () => {
      socket.emit("joinNotification");
    };
    
    // Executar imediatamente se já conectado
    if (socket.connected) {
      onConnect();
    }
    socket.on("connect", onConnect);
    
    // Novo callback
    const handleNewCallback = (data) => {
      setCallbacks((prev) => {
        // Evitar duplicados
        if (prev.find((c) => c.id === data.ticketId)) {
          return prev;
        }
        return [data, ...prev];
      });
      
      // Mostrar notificação
      showToast({
        type: "info",
        message: `📞 Novo callback: ${formatPhoneNumber(data.callbackNumber)}`,
      });
    };
    
    // Callback atualizado
    const handleUpdateCallback = (data) => {
      setCallbacks((prev) => 
        prev.map((c) => 
          c.id === data.ticketId ? { ...c, ...data } : c
        )
      );
    };
    
    // Callback completado/expirado
    const handleRemoveCallback = (data) => {
      setCallbacks((prev) => prev.filter((c) => c.id !== data.ticketId));
    };
    
    // Callback pronto para ligar (ramal disponível)
    const handleReadyCallback = (data) => {
      // Atualizar status
      setCallbacks((prev) => 
        prev.map((c) => 
          c.id === data.ticketId 
            ? { ...c, callbackStatus: "ready_to_call", urgent: true } 
            : c
        )
      );
      
      // Mostrar notificação urgente
      if (data.urgent) {
        showToast({
          type: "warning",
          message: `📞 RAMAL DISPONÍVEL! Callback ${formatPhoneNumber(data.callbackNumber)} pronto para ligar`,
        });

        try {
          soundService.play();
        } catch (e) {
          // Ignorar erros de áudio
        }
      }
    };
    
    // Callback aceito por outro atendente
    const handleTakenCallback = (data) => {
      // Remover da lista pois outro atendente assumiu
      setCallbacks((prev) => prev.filter((c) => c.id !== data.ticketId));
      
      if (data.acceptedByName) {
        showToast({
          type: "info",
          message: `Callback assumido por ${data.acceptedByName}`,
        });
      }
    };
    
    // Callback adiado (snoozed)
    const handleSnoozedCallback = (data) => {
      // Remover temporariamente da lista
      setCallbacks((prev) => prev.filter((c) => c.id !== data.ticketId));
    };
    
    // Callback cancelado
    const handleCanceledCallback = (data) => {
      setCallbacks((prev) => prev.filter((c) => c.id !== data.ticketId));
    };
    
    socket.on("callback:created", handleNewCallback);
    socket.on("callback:notify", handleNewCallback);
    socket.on("callback:ready", handleReadyCallback);
    socket.on("callback:updated", handleUpdateCallback);
    socket.on("callback:completed", handleRemoveCallback);
    socket.on("callback:expired", handleRemoveCallback);
    socket.on("callback:taken", handleTakenCallback);
    socket.on("callback:snoozed", handleSnoozedCallback);
    socket.on("callback:canceled", handleCanceledCallback);
    
    return () => {
      socket.off("connect", onConnect);
      socket.off("callback:created", handleNewCallback);
      socket.off("callback:notify", handleNewCallback);
      socket.off("callback:ready", handleReadyCallback);
      socket.off("callback:updated", handleUpdateCallback);
      socket.off("callback:completed", handleRemoveCallback);
      socket.off("callback:expired", handleRemoveCallback);
      socket.off("callback:taken", handleTakenCallback);
      socket.off("callback:snoozed", handleSnoozedCallback);
      socket.off("callback:canceled", handleCanceledCallback);
    };
  }, [user?.companyId, showToast, hasVoip, socket]);
  
  // Handler: Abrir popover
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    loadCallbacks(); // Recarregar ao abrir
  };
  
  // Handler: Fechar popover
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Handler: Ligar agora (FASE 4: Click-to-Call)
  const handleCall = async (callback) => {
    try {
      setActionLoading(true);
      
      // Chamar endpoint de initiate (FASE 4)
      // Este endpoint verifica disponibilidade do ramal e origina a chamada via Voice AI
      const { data } = await api.post(`/api/callbacks/${callback.id}/initiate`);
      
      if (data.success) {
        showToast({
          type: "success",
          message: data.message || "📞 Ligação iniciada! Aguarde...",
        });
        
        // Remover da lista local (será atualizado via Socket quando completar)
        setCallbacks((prev) => prev.filter((c) => c.id !== callback.id));
      } else {
        showToast({
          type: "error",
          message: data.message || "Não foi possível iniciar a ligação",
        });
      }
      
    } catch (error) {
      console.error("Erro ao iniciar callback:", error);
      const errorMessage = error.response?.data?.message || "Erro ao iniciar ligação";
      showToast({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handler: Adiar (snooze) - Usa novo endpoint POST /snooze
  const handleSnooze = async (callbackId, minutes = 30) => {
    try {
      setActionLoading(true);
      
      // Usar o novo endpoint de snooze
      await api.post(`/api/callbacks/${callbackId}/snooze`, {
        minutes
      });
      
      showToast({
        type: "info",
        message: `Callback adiado por ${minutes} minutos.`,
      });
      
      // Remover temporariamente da lista (reaparecerá quando o horário chegar)
      setCallbacks((prev) => prev.filter((c) => c.id !== callbackId));
      
    } catch (error) {
      console.error("Erro ao adiar callback:", error);
      showToast({
        type: "error",
        message: error.response?.data?.message || "Erro ao adiar callback",
      });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handler: Dispensar - Usa novo endpoint POST /cancel
  const handleDismiss = async (callbackId) => {
    try {
      setActionLoading(true);
      
      // Usar o novo endpoint de cancel
      await api.post(`/api/callbacks/${callbackId}/cancel`, {
        reason: "Dispensado pelo atendente via widget"
      });
      
      showToast({
        type: "warning",
        message: "Callback cancelado.",
      });
      
      // Remover da lista
      setCallbacks((prev) => prev.filter((c) => c.id !== callbackId));
      
    } catch (error) {
      console.error("Erro ao cancelar callback:", error);
      showToast({
        type: "error",
        message: error.response?.data?.message || "Erro ao cancelar callback",
      });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handler: Aceitar callback (assumir responsabilidade)
  // eslint-disable-next-line no-unused-vars
  const handleAccept = async (callbackId) => {
    try {
      setActionLoading(true);
      
      // Usar o novo endpoint de accept
      await api.post(`/api/callbacks/${callbackId}/accept`);
      
      showToast({
        type: "success",
        message: "Callback aceito! Você é o responsável por retornar.",
      });
      
      // Atualizar na lista
      setCallbacks((prev) =>
        prev.map((c) =>
          c.id === callbackId
            ? { ...c, callbackStatus: "ready_to_call" }
            : c
        )
      );
      
    } catch (error) {
      console.error("Erro ao aceitar callback:", error);
      showToast({
        type: "error",
        message: error.response?.data?.message || "Erro ao aceitar callback",
      });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Contador para badge
  const pendingCount = callbacks.length;

  // Não exibir widget se VoIP não está habilitado no perfil do usuário
  if (!hasVoip) return null;
  
  return (
    <>
      {/* Botão com badge - usando padrão do sistema */}
      <Badge
        badgeContent={pendingCount}
        color="error"
        invisible={pendingCount === 0}
        overlap="circular"
      >
        <IconButtonWithTooltip
          icon={PhoneIncoming}
          tooltip={pendingCount > 0 ? `${pendingCount} callback(s) pendente(s)` : "Callbacks"}
          onClick={handleClick}
          aria-describedby={id}
          aria-label="Callbacks"
          iconColor={pendingCount > 0 ? "#ff9800" : undefined}
        />
      </Badge>
      
      {/* Popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        {/* Header */}
        <Box className={classes.popoverHeader}>
          <Box className={classes.headerIcon}>
            <PhoneIncoming size={14} />
          </Box>
          <Box className={classes.headerContent}>
            <Typography className={classes.headerTitle}>
              Callbacks Pendentes
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {pendingCount === 0 
                ? "Nenhum callback aguardando"
                : `${pendingCount} aguardando retorno`
              }
            </Typography>
          </Box>
          <Box className={classes.headerActions}>
            <IconButton
              size="small"
              className={classes.headerButton}
              onClick={loadCallbacks}
              disabled={loading}
            >
              <RefreshCw size={14} />
            </IconButton>
            <IconButton
              size="small"
              className={classes.headerButton}
              onClick={handleClose}
              style={{ padding: 2 }}
            >
              <X size={14} />
            </IconButton>
          </Box>
        </Box>
        
        {/* Lista de callbacks */}
        <Box className={classes.listContainer}>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={32} />
            </Box>
          ) : pendingCount === 0 ? (
            <Box className={classes.emptyState}>
              <CheckCircle2 className={classes.emptyIcon} size={32} />
              <Typography style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                Nenhum callback pendente
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: "0.75rem" }}>
                Novos callbacks aparecerão aqui automaticamente
              </Typography>
            </Box>
          ) : (
            callbacks.map((callback) => (
              <CallbackCard
                key={callback.id}
                callback={callback}
                onCall={handleCall}
                onSnooze={handleSnooze}
                onDismiss={handleDismiss}
                loading={actionLoading}
              />
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

export default CallbackWidget;
